import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Loader2, PhoneCall, ShieldCheck, ChevronDown, ChevronRight, RefreshCw, ShoppingCart, XCircle, Ban, Link2, RotateCcw, FileText, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

const VSTATUS = {
  not_started: "bg-slate-100 text-slate-500", pending: "bg-amber-100 text-amber-700",
  under_review: "bg-blue-100 text-blue-700", verified: "bg-emerald-100 text-emerald-700",
  action_needed: "bg-red-100 text-red-700",
};

const fmt = (d) => d ? new Date(d).toLocaleDateString() : "—";

function Metric({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-900", emerald: "text-emerald-600", blue: "text-blue-600", amber: "text-amber-600" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <p className={`font-heading text-2xl font-extrabold ${tones[tone]}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function AdminPhoneNumbers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ service_available: false });
  const [analytics, setAnalytics] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [working, setWorking] = useState("");
  const [docs, setDocs] = useState(null);

  const load = () => adminApi.get("/admin/phone-numbers").then((r) => {
    setRows(r.data.numbers || []); setMeta({ service_available: r.data.service_available }); setLoading(false);
  }).catch(() => setLoading(false));
  const loadAnalytics = () => adminApi.get("/admin/twilio/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  useEffect(() => { load(); loadAnalytics(); }, []);

  const action = async (act, row) => {
    if (act === "view_customer") { navigate(`/admin/customers/${row.user_id}`); return; }
    setWorking(`${act}-${row.user_id}`);
    try {
      const r = await adminApi.post("/admin/phone-numbers/action", { action: act, uid: row.user_id, number: row.number });
      if (act === "view_documents") { setDocs({ ...r.data.documents, business: row.business_name }); return; }
      toast[r.data.ok ? "success" : "info"](r.data.message || "Done");
      load(); loadAnalytics();
    } catch { toast.error("Action failed"); }
    finally { setWorking(""); }
  };

  const ActBtn = ({ act, row, icon: Icon, label, danger }) => (
    <Button data-testid={`act-${act}`} variant="outline" size="sm" disabled={!!working} onClick={() => action(act, row)}
      className={`gap-1.5 text-xs ${danger ? "text-red-600 border-red-200 hover:bg-red-50" : ""}`}>
      {working === `${act}-${row.user_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {label}
    </Button>
  );

  return (
    <div data-testid="admin-phone-view">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Phone className="h-6 w-6 text-blue-600" /> Phone Numbers</h1>
        <p className="text-slate-500 mt-1">Every customer's number, verification and telephony account — full visibility and control.</p>
      </div>

      {!meta.service_available && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700" data-testid="twilio-not-connected">
          Twilio credentials are not connected yet.
        </div>
      )}

      {/* Analytics */}
      {analytics && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="twilio-analytics">
          <Metric label="Active numbers" value={analytics.active_numbers} tone="emerald" />
          <Metric label="Pending" value={analytics.pending} tone="amber" />
          <Metric label="Under review" value={analytics.under_review} tone="blue" />
          <Metric label="Awaiting number" value={analytics.awaiting_number} tone="amber" />
          <Metric label="Activated today" value={analytics.activated_today} tone="emerald" />
          <Metric label="Avg activation (h)" value={analytics.avg_activation_hours ?? "—"} />
        </div>
      )}

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="w-8"></th>
              <th className="text-left font-semibold px-4 py-3">Customer</th>
              <th className="text-left font-semibold px-4 py-3">Type</th>
              <th className="text-left font-semibold px-4 py-3">Verification</th>
              <th className="text-left font-semibold px-4 py-3">Number</th>
              <th className="text-left font-semibold px-4 py-3">AI</th>
              <th className="text-left font-semibold px-4 py-3">Calls</th>
              <th className="text-left font-semibold px-4 py-3">Mins</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-slate-400">No phone numbers yet.</td></tr>}
            {!loading && rows.map((r) => (
              <Fragment key={r.user_id}>
                <tr data-testid="phone-row" className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === r.user_id ? null : r.user_id)}>
                  <td className="pl-3 text-slate-400">{expanded === r.user_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                  <td className="px-4 py-3"><p className="font-semibold text-slate-800">{r.business_name}</p><p className="text-xs text-slate-400">{r.customer_name}</p></td>
                  <td className="px-4 py-3 capitalize text-slate-600">{r.verification_type || "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full text-xs font-semibold px-2 py-0.5 ${VSTATUS[r.verification_status] || VSTATUS.not_started}`}>{(r.verification_status || "not_started").replace("_", " ")}</span></td>
                  <td className="px-4 py-3 font-mono text-slate-700">{r.number}{r.vapi_connected && <ShieldCheck className="inline h-3.5 w-3.5 text-emerald-500 ml-1" />}</td>
                  <td className="px-4 py-3">{r.live ? <span className="text-emerald-600 text-xs font-semibold">Live</span> : <span className="text-slate-400 text-xs font-semibold">—</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{r.calls}</td>
                  <td className="px-4 py-3 text-slate-600">{r.minutes_used}</td>
                </tr>
                {expanded === r.user_id && (
                  <tr data-testid="phone-row-detail" className="bg-slate-50/60">
                    <td colSpan={8} className="px-6 py-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs mb-4">
                        <Field label="Customer email" value={r.customer_email} />
                        <Field label="Number type" value={r.type} />
                        <Field label="Submission date" value={fmt(r.submission_date)} />
                        <Field label="Approval date" value={fmt(r.approval_date)} />
                        <Field label="Activated" value={fmt(r.activated_at)} />
                        <Field label="Preferred area" value={r.preferred_area_code || "—"} />
                        <Field label="Subaccount SID" value={r.subaccount_sid || "—"} mono />
                        <Field label="Bundle SID" value={r.bundle_sid || "—"} mono />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <ActBtn act="view_customer" row={r} icon={UserCog} label="View customer" />
                        <ActBtn act="refresh" row={r} icon={RefreshCw} label="Refresh status" />
                        <ActBtn act="purchase" row={r} icon={ShoppingCart} label="Purchase number" />
                        <ActBtn act="reconnect_vapi" row={r} icon={Link2} label="Reconnect AI" />
                        <ActBtn act="resubmit" row={r} icon={RotateCcw} label="Resubmit" />
                        <ActBtn act="view_documents" row={r} icon={FileText} label="View documents" />
                        <ActBtn act="suspend" row={r} icon={Ban} label="Suspend" danger />
                        <ActBtn act="release" row={r} icon={XCircle} label="Release" danger />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Documents modal */}
      {docs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDocs(null)} data-testid="docs-modal">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Verification documents — {docs.business}</h2>
            <div className="mt-4 space-y-2 text-xs font-mono">
              <Field label="Entity type" value={docs.entity_type} />
              <Field label="Subaccount SID" value={docs.subaccount_sid || "—"} mono />
              <Field label="Bundle SID" value={docs.bundle_sid || "—"} mono />
              <Field label="End user SID" value={docs.end_user_sid || "—"} mono />
              <Field label="Address SID" value={docs.address_sid || "—"} mono />
              <Field label="Document SIDs" value={(docs.document_sids || []).join(", ") || "—"} mono />
            </div>
            <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold mb-1">Submitted details</p>
              <pre className="whitespace-pre-wrap">{JSON.stringify(docs.details || {}, null, 1)}</pre>
            </div>
            <Button onClick={() => setDocs(null)} className="mt-4 w-full" data-testid="close-docs">Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <span className="text-slate-400">{label}: </span>
      <span className={`text-slate-700 ${mono ? "font-mono break-all" : ""}`}>{value}</span>
    </div>
  );
}
